import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'react-toast';
import AuthPageShell from './AuthPageShell';
import { AnimatedButton } from '../animations/AnimatedFormElements';
import ResendVerificationEmail from './ResendVerificationEmail';
import {
  getPendingVerificationEmail,
  clearPendingVerificationEmail,
} from '../../utils/verificationEmailUtils';
import '../../styles/common.css';
import '../../styles/animations.css';

const API_BASE_URL = process.env.REACT_APP_BASE_URL;
const ONBOARDING_LOGIN_PATH = '/get-started/auth?tab=signin';

const VerifyEmail = () => {
  const navigate = useNavigate();
  const { token } = useParams();
  const [status, setStatus] = useState('verifying'); // verifying | success | error
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setStatus('error');
        setErrorMessage(
          'Invalid or missing verification link. Please register again or contact support.'
        );
        return;
      }

      try {
        const response = await axios.get(`${API_BASE_URL}/users/verifyEmail`, {
          params: {
            email_validation_token: token,
          },
          headers: {
            accept: 'application/json',
          },
        });

        if (response.data?.code === 200 && response.data?.detail?.success) {
          setStatus('success');
          clearPendingVerificationEmail();
          toast.success(
            response.data?.detail?.message ||
              'Email verified successfully! You can now log in.'
          );
          setTimeout(() => {
            navigate(ONBOARDING_LOGIN_PATH, { replace: true });
          }, 2000);
        } else {
          setStatus('error');
          setErrorMessage(
            response.data?.detail?.message ||
              'This verification link is invalid or has expired.'
          );
        }
      } catch (error) {
        console.error('Email verification error:', error);
        setStatus('error');
        const detail = error.response?.data?.detail;
        setErrorMessage(
          (typeof detail === 'object' ? detail?.message : detail) ||
            error.response?.data?.message ||
            'Failed to verify your email. The link may be invalid or expired.'
        );
      }
    };

    verifyEmail();
  }, [token, navigate]);

  const title =
    status === 'verifying'
      ? 'Verifying Email'
      : status === 'success'
        ? 'Email Verified'
        : 'Verification Failed';

  const subtitle =
    status === 'verifying'
      ? 'Please wait while we confirm your email address...'
      : status === 'success'
        ? 'Your email has been verified. Redirecting you to sign in...'
        : 'We could not verify your email address.';

  return (
    <AuthPageShell title={title} subtitle={subtitle} bodyStyle={{ textAlign: 'center' }}>
      {status === 'verifying' && (
        <div className="loading-container">
          <div className="auth-spinner" aria-hidden />
          <p className="auth-muted">Verifying…</p>
        </div>
      )}

      {status === 'success' && (
        <div className="auth-status-icon auth-status-icon--success" aria-hidden>
          ✓
        </div>
      )}

      {status === 'error' && (
        <>
          <div className="auth-status-icon auth-status-icon--error" aria-hidden>
            !
          </div>
          <p className="form-error mb-3">{errorMessage}</p>
          <p className="auth-muted mb-4">
            Enter your email address below and we&apos;ll send you a new verification link.
          </p>
          <div className="resend-verification-info mb-4" style={{ textAlign: 'left' }}>
            <ResendVerificationEmail
              defaultEmail={getPendingVerificationEmail()}
              showEmailInput
              variant="compact"
            />
          </div>
          <AnimatedButton
            type="button"
            className="btn btn-link"
            onClick={() => navigate(ONBOARDING_LOGIN_PATH)}
          >
            Go to Login
          </AnimatedButton>
        </>
      )}
    </AuthPageShell>
  );
};

export default VerifyEmail;
